import * as React from "react";
import styled from "styled-components";

import i18n from "../../lib/i18n";
import { Post } from "../entities";
import { colors } from "../../common/utils/style";
import Icon from "../../common/components/Icon";
import Translate from "../../common/components/Translate";
import PostSelectBoxList from "./PostSelectBoxList";

interface Props {
  options: Post[];
  value: Post | null;
  searchText: string;
  newPostLink?: string;
  isLoading: boolean;
  locale: string;
  onChange: (post: Post | null) => void;
  onChangeSearchText: (text: string) => void;
  onCancel: () => void;
}

interface State {
  isInputFocused: boolean;
}

class PostSelectBox extends React.Component<Props, State> {
  private base: HTMLElement | null = null;
  private input: HTMLInputElement | null = null;
  private list: PostSelectBoxList | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      isInputFocused: false,
    };
  }

  public onKeyDown = (e: React.KeyboardEvent<any>) => {
    let move = 0;
    if (e.key === "ArrowDown") {
      move = 1;
    } else if (e.key === "ArrowUp") {
      move = -1;
    }
    if (move === 0) {
      return;
    }
    const focusedElem = document.activeElement;
    const focusedIndex = this.list!.items.findIndex(item => item && item.base === focusedElem) + move;
    if (focusedIndex === -1) {
      this.input!.focus();
      if (this.input!.select) {
        setTimeout(() => {
          this.input!.select();
        }, 1);
      }
    } else if (this.list!.items[focusedIndex]) {
      this.list!.items[focusedIndex].focus();
    }
  };

  public render() {
    const {
      options,
      value,
      newPostLink,
      isLoading,
      onChange,
      searchText,
      onChangeSearchText,
      onCancel,
      locale,
    } = this.props;

    const { isInputFocused } = this.state;

    return (
      <Base innerRef={el => (this.base = el)} onKeyDown={this.onKeyDown} role="searchbox" tabIndex={-1}>
        <Header>
          <InputWrapper>
            <Input
              innerRef={r => (this.input = r)}
              value={searchText}
              onChange={e => onChangeSearchText(e.target.value)}
              onFocus={() => this.setState({ isInputFocused: true })}
              onBlur={() => this.setState({ isInputFocused: false })}
              placeholder={i18n.translate("company_feed.pin.search_placeholder", { locale })}
            />
            {!isInputFocused ? <SearchIcon name="wt-icon-search-short" /> : null}
            {searchText ? (
              <ClearIcon
                name="wt-icon-close"
                role="button"
                tabIndex={-1}
                onClick={() => {
                  onChangeSearchText("");
                }}
              />
            ) : null}
          </InputWrapper>
          <Complete role="button" tabIndex={-1} onClick={onCancel}>
            <Translate id="company_feed.pin.cancel" />
          </Complete>
        </Header>
        <PostSelectBoxList
          ref={r => (this.list = r!)}
          {...{ options, value, newPostLink, isLoading, onChange }}
          isFiltered={!!searchText}
        />
      </Base>
    );
  }
}

const Base = styled.div`
  width: 340px;
  background-color: white;
  border-radius: 3px;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${colors.lightgray1};
  display: flex;
`;

const InputWrapper = styled.div`
  position: relative;
  width: auto;
  flex-grow: 2;
`;

const Input = styled.input`
  width: 100%;
  padding: 1px 0 1px 32px;
  background: ${colors.lightergray2};
  border: none;
  border-radius: 2px;
  line-height: 30px;
  box-sizing: border-box;
  transition: 100ms padding linear;
  &:focus {
    outline: none;
    padding-left: 12px;
  }
`;

const SearchIcon = styled(Icon)`
  position: absolute;
  left: 7px;
  top: 4px;
  font-size: 18px;
  color: ${colors.gray1};
  pointer-events: none;
`;

const ClearIcon = styled(Icon)`
  position: absolute;
  right: 7px;
  top: 4px;
  color: ${colors.gray1};
  cursor: pointer;
`;

const Complete = styled.div`
  flex-shrink: 0;
  padding: 0;
  margin-left: 18px;
  text-align: center;
  color: ${colors.blue1};
  font-size: 14px;
  font-weight: 600;
  line-height: 32px;
  cursor: pointer;
  outline: none;
`;

export default PostSelectBox;



// WEBPACK FOOTER //
// ./frontend/src/posts/components/PostSelectBox.tsx